import React, { useContext } from 'react';
import { languageContext } from '@/context/language.context';
import t from '@/utils/Language/UI-translations';
import Button from '@/components/Button/Button';
import { modalContext } from '@/context/modal.context';
import { referralContext } from '@/context/referral.context';
import { viewportContext } from '@/context/viewport.context';
import useAccount from '@/hooks/useAccount';
import { resourceHelpers } from '@/utils/helpers';
import ODRS from '@/utils/api/odrs';
import classes from './ReferButton.module.scss';

const ReferButton = ({
  resource,
  buttonType,
  buttonShape,
  buttonBorder,
  additionalClasses,
  showButtonTextOnMobile = false,
  showButtonText = true,
  source = ''
}) => {
  const {
    addReferredOpportunity,
    getReferralClient,
    inReferralState,
    isReferred,
    referredOpportunities,
    removeReferredOpportunity,
    updateInReferralState
  } = useContext(referralContext);
  const { enqueueModal, closeModal } = useContext(modalContext);
  const { currentLanguage } = useContext(languageContext);
  const { account } = useAccount();
  const { isXs } = useContext(viewportContext);

  // TODO replace with domain solution
  //changed the regexp to include more, but this all seems hacky
  const project = typeof window !== 'undefined' && /ecm-screening-for-/.test(window.location.href) ? 'interface' : '';

  const isUnavailable = () => {
    return resource.resource_type === 'Opportunity' && resourceHelpers.isCurrentlyUnavailable(resource);
  };

  const isOrganization = resource.resource_type === 'Organization';

  const handleReferralProcess = (e) => {
    if (!account.state.isLoggedIn) {
      return enqueueModal({ name: 'LoginModal', reason: 'refer' }, { forceOpen: true });
    }
    if (!inReferralState && project != 'interface') {
      openEditClientModal();
    } else {
      if (isOrganization) {
        openSelectOpportunityModal(getReferralClient());
      } else {
        handleReferrals(getReferralClient());
      }
    }
  };

  const handleReferrals = (client) => {
    updateInReferralState(true, client, source);
    if (isReferred(resource)) {
      removeReferredOpportunity(resource);
    } else {
      // Some list endpoints (e.g. org opportunities list) do not return intake_assessment.
      // Fetch the individual opportunity to ensure it is present before storing in the cart.
      if (resource.resource_type === 'Opportunity' && resource.intake_assessment === undefined) {
        ODRS.getResourceById('opportunities', resource.id, { locale: currentLanguage })
          .then((resp) => {
            resource.intake_assessment = resp.data?.intake_assessment;
            addReferredOpportunity(resource);
          })
          .catch(() => {
            addReferredOpportunity(resource);
          });
      } else {
        addReferredOpportunity(resource);
      }
      closeModal();
    }
  };

  const handleOrgReferrals = (client, opportunities) => {
    updateInReferralState(true, client, source);
    const selectedIds = opportunities.map((opp) => opp.id);
    // remove org opps that were deselected
    referredOpportunities
      .filter((opp) => opp.organization?.id === resource.id && !selectedIds.includes(opp.id))
      .forEach((opp) => removeReferredOpportunity(opp));
    // add newly selected opps
    opportunities.forEach((opp) => {
      if (!isReferred(opp)) {
        addReferredOpportunity(opp);
      }
    });
  };

  const openEditClientModal = () => {
    enqueueModal({
      name: 'EditClientModal',
      agencyClient: {},
      orgNetwork: {},
      showSalesforce: false,
      actionType: 'searchClients',
      handleSelectedClient: isOrganization ? (client) => openSelectOpportunityModal(client, { closeCurrent: true }) : handleReferrals
    });
  };

  const openSelectOpportunityModal = (client, { closeCurrent = false } = {}) => {
    const initialSelectedIds = referredOpportunities.filter((opp) => opp.organization?.id === resource.id).map((opp) => opp.id);
    if (closeCurrent) closeModal();
    enqueueModal(
      {
        name: 'SelectOpportunityModal',
        organization: resource,
        initialSelectedIds,
        onRefer: (opportunities) => handleOrgReferrals(client, opportunities)
      },
      { forceOpen: !closeCurrent }
    );
  };

  const buttonText = (resource) => {
    if (!showButtonText) return '';
    if (!showButtonTextOnMobile && isXs) {
      return '';
    } else if (isReferred(resource)) {
      return t('Added');
    } else {
      return t('Refer');
    }
  };

  if (isUnavailable()) {
    return null;
  }

  return (
    <Button
      type={buttonType || 'default'}
      border={buttonBorder || false}
      shape={buttonShape || 'pill'}
      icon={{ style: 'fas', font: isReferred(resource) ? 'check' : 'share' }}
      additionalClasses={isReferred(resource) ? `${classes.addedIcon} ${additionalClasses}` : additionalClasses}
      text={buttonText(resource)}
      onButtonClick={handleReferralProcess}
    />
  );
};

export default ReferButton;
